import { apiClient, getAuthToken } from "./authService";

type WebPushRegistrationStatus =
  | "registered"
  | "unsupported"
  | "insecure-context"
  | "missing-token"
  | "permission-denied"
  | "missing-vapid-public-key";

export type WebPushRegistrationResult = {
  status: WebPushRegistrationStatus;
  endpoint?: string;
};

type VapidPublicKeyResponse = {
  publicKey?: string;
  public_key?: string;
  vapidPublicKey?: string;
  vapid_public_key?: string;
  key?: string;
  data?: VapidPublicKeyResponse;
  result?: VapidPublicKeyResponse;
};

export type AdminNotificationDevice = {
  id: string;
  deviceType?: string;
  webPushSubscription?: PushSubscriptionJSON & { endpoint?: string };
  endpoint?: string;
  meta?: {
    browser?: string;
    os?: string;
    label?: string;
    [key: string]: any;
  };
  enabled?: boolean;
  active?: boolean;
  createdAt?: string;
  created_at?: string;
  updatedAt?: string;
  updated_at?: string;
  [key: string]: any;
};

export type DisableCurrentWebPushResult = {
  status: "disabled" | "unsupported" | "missing-subscription" | "device-not-found";
  deviceId?: string;
};

const SERVICE_WORKER_URL = "/sw.js";
const CURRENT_DEVICE_ID_STORAGE_KEY = "admin_notification_device_id";
const CURRENT_DEVICE_ENDPOINT_STORAGE_KEY = "admin_notification_device_endpoint";

let registrationInFlight: Promise<WebPushRegistrationResult> | null = null;

export const browserSupportsWebPush = (): boolean => {
  if (typeof window === "undefined" || typeof navigator === "undefined") {
    return false;
  }

  return (
    "serviceWorker" in navigator &&
    "PushManager" in window &&
    "Notification" in window
  );
};

const detectBrowserName = (): string => {
  const userAgent = navigator.userAgent;
  if (/Edg\//i.test(userAgent)) return "Edge";
  if (/Chrome\//i.test(userAgent) && !/Chromium/i.test(userAgent)) {
    return "Chrome";
  }
  if (/Firefox\//i.test(userAgent)) return "Firefox";
  if (/Safari\//i.test(userAgent) && !/Chrome\//i.test(userAgent)) {
    return "Safari";
  }

  return "Browser";
};

const detectOsName = (): string => {
  const userAgent = navigator.userAgent;
  if (/Mac OS X/i.test(userAgent)) return "macOS";
  if (/iPhone|iPad|iPod/i.test(userAgent)) return "iOS";
  if (/Android/i.test(userAgent)) return "Android";
  if (/Windows/i.test(userAgent)) return "Windows";
  if (/Linux/i.test(userAgent)) return "Linux";

  return "Unknown OS";
};

const getDeviceMeta = () => {
  const browser = detectBrowserName();
  const os = detectOsName();

  return {
    browser,
    os,
    label: `${os} ${browser}`,
  };
};

const getDeviceIdFromPayload = (payload: any): string => {
  return pickNonEmptyString(
    payload?.id,
    payload?._id,
    payload?.deviceId,
    payload?.device_id,
    payload?.data?.id,
    payload?.data?._id,
    payload?.data?.deviceId,
    payload?.data?.device_id,
    payload?.device?.id,
    payload?.device?._id,
    payload?.result?.id,
    payload?.result?._id,
  );
};

export const getStoredCurrentNotificationDeviceId = (): string => {
  try {
    return localStorage.getItem(CURRENT_DEVICE_ID_STORAGE_KEY) || "";
  } catch {
    return "";
  }
};

const storeCurrentNotificationDevice = (deviceId: string, endpoint: string) => {
  try {
    if (deviceId) {
      localStorage.setItem(CURRENT_DEVICE_ID_STORAGE_KEY, deviceId);
    }
    if (endpoint) {
      localStorage.setItem(CURRENT_DEVICE_ENDPOINT_STORAGE_KEY, endpoint);
    }
  } catch {
    // Ignore storage failures.
  }
};

const clearStoredCurrentNotificationDevice = () => {
  try {
    localStorage.removeItem(CURRENT_DEVICE_ID_STORAGE_KEY);
    localStorage.removeItem(CURRENT_DEVICE_ENDPOINT_STORAGE_KEY);
  } catch {
    // Ignore storage failures.
  }
};

export function urlBase64ToUint8Array(base64String: string): Uint8Array {
  const padding = "=".repeat((4 - (base64String.length % 4)) % 4);
  const base64 = (base64String + padding)
    .replace(/-/g, "+")
    .replace(/_/g, "/");
  const rawData = window.atob(base64);

  return Uint8Array.from(
    Array.from(rawData).map((char) => char.charCodeAt(0))
  );
}

const pickNonEmptyString = (...values: any[]): string => {
  for (const value of values) {
    if (typeof value === "string" && value.trim()) {
      return value.trim();
    }
  }

  return "";
};

const extractVapidPublicKey = (payload: VapidPublicKeyResponse | string): string => {
  if (typeof payload === "string") return payload.trim();

  return pickNonEmptyString(
    payload?.publicKey,
    payload?.vapidPublicKey,
    payload?.vapid_public_key,
    payload?.public_key,
    payload?.key,
    payload?.data?.publicKey,
    payload?.data?.vapidPublicKey,
    payload?.data?.vapid_public_key,
    payload?.data?.public_key,
    payload?.data?.key,
    payload?.result?.publicKey,
    payload?.result?.vapidPublicKey,
    payload?.result?.vapid_public_key,
    payload?.result?.public_key,
    payload?.result?.key
  );
};

const getVapidPublicKey = async (): Promise<string> => {
  const response = await apiClient.get<VapidPublicKeyResponse | string>(
    "/notification/devices/web-push/vapid-public-key",
    { suppressToast: true } as any
  );

  return extractVapidPublicKey(response.data);
};

const bufferSourceToUint8Array = (
  value: BufferSource | null | undefined
): Uint8Array | null => {
  if (!value) return null;
  if (value instanceof ArrayBuffer) return new Uint8Array(value);

  return new Uint8Array(value.buffer, value.byteOffset, value.byteLength);
};

const uint8ArraysMatch = (left: Uint8Array, right: Uint8Array): boolean => {
  if (left.byteLength !== right.byteLength) return false;

  for (let index = 0; index < left.byteLength; index += 1) {
    if (left[index] !== right[index]) return false;
  }

  return true;
};

const subscribeBrowser = async (
  registration: ServiceWorkerRegistration,
  publicKey: string
): Promise<PushSubscription> => {
  const applicationServerKey = urlBase64ToUint8Array(publicKey);
  const existingSubscription = await registration.pushManager.getSubscription();
  if (existingSubscription) {
    const existingApplicationServerKey = bufferSourceToUint8Array(
      existingSubscription.options.applicationServerKey
    );

    if (
      !existingApplicationServerKey ||
      uint8ArraysMatch(existingApplicationServerKey, applicationServerKey)
    ) {
      return existingSubscription;
    }

    await existingSubscription.unsubscribe();
  }

  return registration.pushManager.subscribe({
    userVisibleOnly: true,
    applicationServerKey: applicationServerKey.buffer as ArrayBuffer,
  });
};

const registerWebPushDevice = async (
  subscription: PushSubscription
): Promise<string> => {
  const response = await apiClient.post(
    "/admin/notification-devices/register",
    {
      webPushSubscription:
        typeof subscription.toJSON === "function"
          ? subscription.toJSON()
          : subscription,
      meta: getDeviceMeta(),
    },
    { suppressToast: true } as any
  );

  const deviceId = getDeviceIdFromPayload(response.data);
  storeCurrentNotificationDevice(deviceId, subscription.endpoint);
  return deviceId;
};

const registerBackofficeWebPushInternal =
  async (): Promise<WebPushRegistrationResult> => {
    if (!browserSupportsWebPush()) {
      return { status: "unsupported" };
    }

    if (window.isSecureContext === false) {
      return { status: "insecure-context" };
    }

    const token = getAuthToken();
    if (!token) {
      return { status: "missing-token" };
    }

    await navigator.serviceWorker.register(SERVICE_WORKER_URL);

    const permission = await Notification.requestPermission();
    if (permission !== "granted") {
      return { status: "permission-denied" };
    }

    const registration = await navigator.serviceWorker.ready;

    const publicKey = await getVapidPublicKey();
    if (!publicKey) {
      console.warn(
        "Backoffice web push registration skipped: VAPID public key response did not include a recognized key."
      );
      return { status: "missing-vapid-public-key" };
    }

    const subscription = await subscribeBrowser(registration, publicKey);
    await registerWebPushDevice(subscription);

    return { status: "registered", endpoint: subscription.endpoint };
  };

export const registerBackofficeWebPush =
  async (): Promise<WebPushRegistrationResult> => {
    if (!registrationInFlight) {
      registrationInFlight = registerBackofficeWebPushInternal().finally(() => {
        registrationInFlight = null;
      });
    }

    return registrationInFlight;
  };

const parseMaybeJsonObject = (value: any): any => {
  if (typeof value !== "string") return value;

  try {
    return JSON.parse(value);
  } catch {
    return value;
  }
};

const resolveWebPushSubscription = (item: any): any => {
  return parseMaybeJsonObject(
    item?.webPushSubscription ||
      item?.web_push_subscription ||
      item?.pushSubscription ||
      item?.push_subscription ||
      item?.subscription,
  );
};

const normalizeAdminNotificationDevices = (payload: any): AdminNotificationDevice[] => {
  const list =
    Array.isArray(payload?.devices)
      ? payload.devices
      : Array.isArray(payload?.data)
      ? payload.data
      : Array.isArray(payload?.items)
      ? payload.items
      : Array.isArray(payload)
      ? payload
      : [];

  return list
    .map((item: any) => {
      const webPushSubscription = resolveWebPushSubscription(item);
      const id = String(item?.id || item?._id || item?.deviceId || "");
      return {
        ...item,
        id,
        endpoint:
          item?.endpoint ||
          item?.webPushEndpoint ||
          item?.web_push_endpoint ||
          webPushSubscription?.endpoint ||
          "",
        webPushSubscription,
        meta: item?.meta || {},
      };
    })
    .filter((item: AdminNotificationDevice) => Boolean(item.id));
};

export const getAdminNotificationDevices =
  async (): Promise<AdminNotificationDevice[]> => {
    const response = await apiClient.get("/admin/notification-devices", {
      suppressToast: true,
    } as any);

    return normalizeAdminNotificationDevices(response.data);
  };

export const deleteAdminNotificationDevice = async (
  deviceId: string
): Promise<void> => {
  await apiClient.delete(`/admin/notification-devices/${deviceId}`, {
    suppressToast: true,
  } as any);
};

export const getCurrentWebPushEndpoint = async (): Promise<string> => {
  if (!browserSupportsWebPush()) return "";

  const registration = await navigator.serviceWorker.ready;
  const subscription = await registration.pushManager.getSubscription();

  return subscription?.endpoint || "";
};

export const disableCurrentBrowserWebPush =
  async (knownDeviceId?: string): Promise<DisableCurrentWebPushResult> => {
    if (!browserSupportsWebPush()) {
      return { status: "unsupported" };
    }

    const registration = await navigator.serviceWorker.ready;
    const subscription = await registration.pushManager.getSubscription();
    if (!subscription) {
      clearStoredCurrentNotificationDevice();
      return { status: "missing-subscription" };
    }

    const storedDeviceId = getStoredCurrentNotificationDeviceId();
    const deviceIdToDelete = knownDeviceId || storedDeviceId;
    if (deviceIdToDelete) {
      await deleteAdminNotificationDevice(deviceIdToDelete);
      await subscription.unsubscribe();
      clearStoredCurrentNotificationDevice();
      return { status: "disabled", deviceId: deviceIdToDelete };
    }

    const endpoint = subscription.endpoint;
    const devices = await getAdminNotificationDevices();
    const currentDevice = devices.find((device) => device.endpoint === endpoint);
    if (!currentDevice) {
      await subscription.unsubscribe();
      clearStoredCurrentNotificationDevice();
      return { status: "device-not-found" };
    }

    await deleteAdminNotificationDevice(currentDevice.id);
    await subscription.unsubscribe();
    clearStoredCurrentNotificationDevice();
    return { status: "disabled", deviceId: currentDevice.id };
  };
